import { useEffect, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ImageIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react';

import type { ContentAsset } from '@mds/shared/types/content';
import { cn } from '@mds/shared/lib/utils';

import GalleryPicker from '@/components/files/gallery-picker';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { InlineError, inputCls } from '@/components/ui/field';
import IconButton from '@mds/shared/ui/icon-button';
import Modal from '@/components/ui/modal';
import {
  LIMITS,
  blankCategory,
  blankItem,
  type CategoryDraft,
  type ItemDraft,
} from '@/domain/cafe';
import { formatCents, inputToCents } from '@/lib/money';
import { type FieldBinder, useInlineField } from '@/lib/validation';

/** Moves one entry up or down by one; at the edge the list is returned as it was. */
function move<T>(list: T[], index: number, by: -1 | 1): T[] {
  const to = index + by;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to] as T, next[index] as T];
  return next;
}

const replaceAt = <T,>(list: T[], index: number, value: T) =>
  list.map((x, at) => (at === index ? value : x));

/** "$2.50" for a typed price, or a dash while it is not an amount yet. */
const priceLabel = (typed: string) => {
  const cents = inputToCents(typed);
  return cents === null ? '—' : cents === 0 ? 'Free' : formatCents(cents);
};

/**
 * The menu: ordered categories, each with ordered items.
 *
 * Categories are a list down the side and one is open at a time, so a long
 * menu reads as a table of contents rather than a wall of forms. Inside a
 * category every item is one row — picture, name, price, whether it is on
 * today — and the details open in a dialog. Availability stays on the row,
 * because "we ran out of croissants" is the edit made most often.
 *
 * Array order is display order, so moving a row is the only way to change
 * where it appears on the public page.
 *
 * `reveal` is how Save reaches a problem that is not on screen: the page
 * hands over the path of the first error, and the category holding it is
 * opened before focus moves. A row with a problem marks its Edit button
 * invalid, so Save's focus lands on the control that opens the fix.
 */
export default function MenuEditor({
  categories,
  onChange,
  readOnly,
  urls,
  known,
  field,
  reveal,
}: {
  categories: CategoryDraft[];
  onChange: (next: CategoryDraft[]) => void;
  readOnly: boolean;
  urls: ReadonlyMap<string, string>;
  known: readonly ContentAsset[];
  /** The page's validation, checked against `cafeSchema`. */
  field: FieldBinder;
  /** The draft path Save could not reach, and a counter so the same path can be asked for twice. */
  reveal?: { path: string; at: number } | null;
}) {
  const inline = useInlineField(field);
  const menuLimit = inline('categories');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Which item is open in the dialog, as [category index, item index]. */
  const [editing, setEditing] = useState<[number, number] | null>(null);

  const total = categories.reduce((n, c) => n + c.items.length, 0);
  const unavailable = categories.reduce(
    (n, c) => n + c.items.filter((i) => !i.isAvailable).length,
    0,
  );

  // The open category: the chosen one while it exists, otherwise the first.
  const ci = Math.max(
    0,
    categories.findIndex((c) => c.id === selectedId),
  );
  const category = categories[ci] ?? null;

  // Save found a problem: open the category it is in so focus can reach it.
  // Reads the list through a ref, so the effect runs once per request rather
  // than every time the list changes — which would pin the view to that category.
  const latest = useRef(categories);
  latest.current = categories;
  useEffect(() => {
    if (!reveal) return;
    const match = /^categories\.(\d+)/.exec(reveal.path);
    if (!match) return;
    const target = latest.current[Number(match[1])];
    if (target) setSelectedId(target.id);
  }, [reveal]);

  const setCategory = (at: number, patch: Partial<CategoryDraft>) =>
    onChange(replaceAt(categories, at, { ...(categories[at] as CategoryDraft), ...patch }));

  const setItem = (catAt: number, itemAt: number, patch: Partial<ItemDraft>) => {
    const c = categories[catAt] as CategoryDraft;
    setCategory(catAt, {
      items: replaceAt(c.items, itemAt, { ...(c.items[itemAt] as ItemDraft), ...patch }),
    });
  };

  const addCategory = () => {
    const next = blankCategory();
    next.items = [];
    onChange([...categories, next]);
    setSelectedId(next.id);
  };

  const addItem = () => {
    if (!category) return;
    const item = blankItem();
    setCategory(ci, { items: [...category.items, item] });
    setEditing([ci, category.items.length]);
  };

  const removeItem = (itemAt: number) => {
    if (!category) return;
    setCategory(ci, { items: category.items.filter((_, at) => at !== itemAt) });
    setEditing(null);
  };

  const editingItem = editing !== null ? (categories[editing[0]]?.items[editing[1]] ?? null) : null;
  const itemLimit = inline(`categories.${ci}.items`);
  const atItemLimit =
    category !== null &&
    (category.items.length >= LIMITS.itemsPerCategory || total >= LIMITS.items);

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-base text-ink-muted">
        {categories.length} {categories.length === 1 ? 'category' : 'categories'} · {total}{' '}
        {total === 1 ? 'item' : 'items'}
        {unavailable > 0 && ` · ${unavailable} not available today`}
      </p>

      {categories.length === 0 ? (
        <Card>
          <EmptyState
            title="The menu is empty"
            hint="Add a category (Coffee, Tea, Pastries) and put items in it."
          />
          {!readOnly && (
            <div className="mt-5 flex flex-col items-start gap-2" {...menuLimit.props}>
              <Button onClick={addCategory}>
                <PlusIcon className="size-5" aria-hidden="true" />
                Add category
              </Button>
              <InlineError {...menuLimit.message} />
            </div>
          )}
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[260px_1fr] lg:items-start">
          {/* ------------------------------------------------- categories */}
          <nav
            aria-label="Menu categories"
            className="flex flex-col gap-3 rounded-2xl border border-hairline bg-surface p-3 shadow-card"
          >
            <ul className="m-0 flex list-none gap-1 overflow-x-auto p-0 lg:flex-col lg:overflow-visible">
              {categories.map((c, at) => {
                const problem = field(`categories.${at}`, { within: true }).error;
                const open = at === ci;
                return (
                  <li key={c.id} className="shrink-0 lg:shrink">
                    <button
                      type="button"
                      onClick={() => setSelectedId(c.id)}
                      aria-current={open ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-base transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(--focus-ring-color)',
                        open
                          ? 'bg-cell-selected-bg font-semibold text-cell-selected-fg'
                          : 'text-ink-secondary hover:bg-brand-wash hover:text-brand-text',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {c.name.trim() || <span className="italic">Untitled category</span>}
                      </span>
                      {problem && (
                        <span
                          className="bg-danger size-2.5 shrink-0 rounded-full"
                          aria-label="Needs attention"
                          title="Needs attention"
                        />
                      )}
                      <span
                        className={cn(
                          'shrink-0 font-mono text-sm tabular-nums',
                          open ? 'text-cell-selected-fg/80' : 'text-ink-muted',
                        )}
                      >
                        {c.items.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {!readOnly && (
              <div
                className="flex flex-col items-start gap-2 border-t border-hairline pt-3"
                {...menuLimit.props}
              >
                <Button
                  variant="quiet"
                  disabled={categories.length >= LIMITS.categories}
                  onClick={addCategory}
                >
                  <PlusIcon className="size-5" aria-hidden="true" />
                  Add category
                </Button>
                <InlineError {...menuLimit.message} />
              </div>
            )}
          </nav>

          {/* ---------------------------------------------- open category */}
          {category && (
            <Card
              key={category.id}
              title={category.name.trim() || `Category ${ci + 1}`}
              action={
                !readOnly ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <IconButton
                      label="Move category up"
                      onClick={() => onChange(move(categories, ci, -1))}
                      className={ci === 0 ? 'pointer-events-none opacity-40' : undefined}
                    >
                      <ChevronUpIcon className="size-5" aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      label="Move category down"
                      onClick={() => onChange(move(categories, ci, 1))}
                      className={
                        ci === categories.length - 1 ? 'pointer-events-none opacity-40' : undefined
                      }
                    >
                      <ChevronDownIcon className="size-5" aria-hidden="true" />
                    </IconButton>
                    <Button
                      variant="danger"
                      onClick={() => {
                        onChange(categories.filter((_, at) => at !== ci));
                        setSelectedId(categories[ci + 1]?.id ?? categories[ci - 1]?.id ?? null);
                      }}
                    >
                      Remove category
                    </Button>
                  </div>
                ) : undefined
              }
            >
              <div className="flex flex-col gap-5">
                <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
                  <Field label="Category name" required {...field(`categories.${ci}.name`)}>
                    <input
                      className={inputCls}
                      value={category.name}
                      maxLength={120}
                      disabled={readOnly}
                      placeholder="Coffee"
                      onChange={(e) => setCategory(ci, { name: e.target.value })}
                    />
                  </Field>
                  <Field
                    label="Description"
                    hint="Optional. One line under the category name."
                    {...field(`categories.${ci}.description`)}
                  >
                    <input
                      className={inputCls}
                      value={category.description}
                      maxLength={500}
                      disabled={readOnly}
                      placeholder="Espresso-based drinks."
                      onChange={(e) => setCategory(ci, { description: e.target.value })}
                    />
                  </Field>
                </div>

                {category.items.length === 0 ? (
                  <EmptyState
                    title="No items yet"
                    hint="Add the first thing on this part of the menu."
                  />
                ) : (
                  <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {category.items.map((item, ii) => {
                      const at = `categories.${ci}.items.${ii}`;
                      const problem = field(at, { within: true }).error;
                      const image = item.imageFileId ? urls.get(item.imageFileId) : undefined;
                      return (
                        <li
                          key={item.id}
                          className={cn(
                            'grid grid-cols-[3rem_1fr_auto] items-center gap-3 rounded-xl border px-3 py-2.5 sm:grid-cols-[3rem_1fr_6rem_auto_auto]',
                            item.isAvailable
                              ? 'border-hairline bg-surface'
                              : 'border-dashed border-hairline bg-raised',
                            problem && 'border-danger-edge bg-danger-wash',
                          )}
                        >
                          {image ? (
                            <img
                              src={image}
                              alt=""
                              className="size-12 rounded-lg border border-hairline object-cover"
                            />
                          ) : (
                            <span
                              className="flex size-12 items-center justify-center rounded-lg border border-dashed border-hairline text-ink-muted"
                              aria-hidden="true"
                            >
                              <ImageIcon className="size-5" />
                            </span>
                          )}

                          <div className="flex min-w-0 flex-col">
                            <span
                              className={cn(
                                'truncate text-base font-semibold',
                                !item.isAvailable && 'text-ink-muted',
                              )}
                            >
                              {item.name.trim() || <span className="italic">Unnamed item</span>}
                            </span>
                            {item.description.trim() ? (
                              <span className="truncate text-md text-ink-muted">
                                {item.description}
                              </span>
                            ) : null}
                            {problem && <span className="text-md text-danger-text">{problem}</span>}
                          </div>

                          <span
                            className={cn(
                              'text-right text-base font-semibold tabular-nums',
                              !item.isAvailable && 'text-ink-muted',
                            )}
                          >
                            {priceLabel(item.price)}
                          </span>

                          <label className="col-span-3 flex items-center gap-2 text-base sm:col-span-1">
                            <input
                              type="checkbox"
                              className="h-5 w-5 accent-brand-edge"
                              checked={item.isAvailable}
                              disabled={readOnly}
                              onChange={(e) => setItem(ci, ii, { isAvailable: e.target.checked })}
                            />
                            {item.isAvailable ? (
                              'Available'
                            ) : (
                              <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
                                Sold out
                              </Chip>
                            )}
                          </label>

                          {!readOnly ? (
                            <div className="col-span-3 flex items-center justify-end gap-1 sm:col-span-1">
                              <IconButton
                                label={`Edit ${item.name.trim() || 'item'}`}
                                onClick={() => setEditing([ci, ii])}
                                className={
                                  problem ? 'border-danger-edge text-danger-text' : undefined
                                }
                                aria-invalid={problem ? true : undefined}
                              >
                                <PencilIcon className="size-5" aria-hidden="true" />
                              </IconButton>
                              <IconButton
                                label={`Move ${item.name.trim() || 'item'} up`}
                                onClick={() =>
                                  setCategory(ci, { items: move(category.items, ii, -1) })
                                }
                                className={ii === 0 ? 'pointer-events-none opacity-40' : undefined}
                              >
                                <ChevronUpIcon className="size-5" aria-hidden="true" />
                              </IconButton>
                              <IconButton
                                label={`Move ${item.name.trim() || 'item'} down`}
                                onClick={() =>
                                  setCategory(ci, { items: move(category.items, ii, 1) })
                                }
                                className={
                                  ii === category.items.length - 1
                                    ? 'pointer-events-none opacity-40'
                                    : undefined
                                }
                              >
                                <ChevronDownIcon className="size-5" aria-hidden="true" />
                              </IconButton>
                              <IconButton
                                label={`Remove ${item.name.trim() || 'item'}`}
                                tone="danger"
                                onClick={() => removeItem(ii)}
                              >
                                <Trash2Icon className="size-5" aria-hidden="true" />
                              </IconButton>
                            </div>
                          ) : (
                            <span className="hidden sm:block" />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {!readOnly && (
                  <div
                    role="group"
                    aria-label={`Items in ${category.name.trim() || `category ${ci + 1}`}`}
                    tabIndex={-1}
                    className="flex flex-col items-start gap-2"
                    {...itemLimit.props}
                  >
                    <Button variant="quiet" disabled={atItemLimit} onClick={addItem}>
                      <PlusIcon className="size-5" aria-hidden="true" />
                      Add item
                    </Button>
                    <InlineError {...itemLimit.message} />
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ------------------------------------------------------ item dialog */}
      <Modal
        open={editingItem !== null}
        onClose={() => setEditing(null)}
        title={editingItem?.name.trim() || 'New item'}
        footer={
          editing && (
            <>
              <Button onClick={() => setEditing(null)}>Done</Button>
              <Button variant="danger" onClick={() => removeItem(editing[1])}>
                Remove item
              </Button>
            </>
          )
        }
      >
        {editing && editingItem && (
          <ItemFields
            item={editingItem}
            path={`categories.${editing[0]}.items.${editing[1]}`}
            field={field}
            known={known}
            onChange={(patch) => setItem(editing[0], editing[1], patch)}
          />
        )}
      </Modal>
    </div>
  );
}

/** One item's details, as the dialog shows them. */
function ItemFields({
  item,
  path,
  field,
  known,
  onChange,
}: {
  item: ItemDraft;
  path: string;
  field: FieldBinder;
  known: readonly ContentAsset[];
  onChange: (patch: Partial<ItemDraft>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-[1fr_9rem]">
        <Field label="Name" required {...field(`${path}.name`)}>
          <input
            className={inputCls}
            value={item.name}
            maxLength={160}
            placeholder="Americano"
            autoFocus
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </Field>
        <Field label="Price" required hint="USD. 0 shows as Free." {...field(`${path}.price`)}>
          <div className="relative">
            <span className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-lg text-ink-muted">
              $
            </span>
            <input
              className={inputCls + ' pl-8 text-right tabular-nums'}
              value={item.price}
              inputMode="decimal"
              placeholder="2.50"
              onChange={(e) => onChange({ price: e.target.value })}
            />
          </div>
        </Field>
      </div>
      <Field
        label="Description"
        hint="Optional. Ingredients, size, what makes it worth ordering."
        {...field(`${path}.description`)}
      >
        <textarea
          rows={3}
          className={inputCls}
          value={item.description}
          maxLength={1000}
          placeholder="Double espresso with hot water."
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Field>
      <label className="flex items-center gap-3 text-base text-ink">
        <input
          type="checkbox"
          className="h-5 w-5 accent-brand-edge"
          checked={item.isAvailable}
          onChange={(e) => onChange({ isAvailable: e.target.checked })}
        />
        Available today
        <span className="text-ink-muted">
          (switch off when it has run out; it stays on the menu as sold out)
        </span>
      </label>
      <GalleryPicker
        label="Picture"
        optional
        max={1}
        value={item.imageFileId ? [item.imageFileId] : []}
        onChange={(ids) => onChange({ imageFileId: ids[0] ?? null })}
        known={known}
      />
    </div>
  );
}
